import { Container } from '../ui/Container';
import { Icon } from '../ui/Icon';
import { useShared } from './Layout';
import { t } from '../../i18n/t';
import i18n from '../../i18n/config';

export function Footer() {
  const { brand, products, blogCategories } = useShared();
  const privateProducts = products.filter((product) => product.audiences.includes('private'));
  const businessProducts = products.filter((product) => product.audiences.includes('business'));
  const locale = i18n.locale || 'lv';
  const legalLinks = [
    { to: `https://kreditiem.lv/documents/${locale}/privacy_policy.pdf`, label: t('ui.footer.privacy') },
    { to: `https://kreditiem.lv/documents/${locale}/terms.pdf`, label: t('ui.footer.terms') },
  ];

  return (
    <footer className="bg-primary-700 text-white">
      <Container>
        <div className="grid gap-10 py-12 sm:grid-cols-2 lg:grid-cols-4 lg:py-16">
          <div>
            <p className="font-display text-lg font-bold text-white">{brand.name}</p>
            <p className="mt-2 text-sm leading-relaxed text-gray-300">{brand.tagline}</p>

            <ul className="mt-5 space-y-3 text-sm text-gray-300">
              <li>
                <a href={brand.phoneHref} className="inline-flex items-center gap-2 hover:text-warning-300">
                  <Icon name="phone" className="h-4 w-4 text-accent-400" />
                  {brand.phone}
                </a>
              </li>
              <li>
                <a href={`mailto:${brand.email}`} className="inline-flex items-center gap-2 hover:text-warning-300">
                  <Icon name="mail" className="h-4 w-4 text-accent-400" />
                  {brand.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Icon name="clock" className="mt-0.5 h-4 w-4 shrink-0 text-accent-400" />
                {brand.workingHours}
              </li>
              <li className="flex items-start gap-2">
                <Icon name="map-pin" className="mt-0.5 h-4 w-4 shrink-0 text-accent-400" />
                {brand.address}
              </li>
            </ul>
          </div>

          <FooterColumn title={t('ui.footer.private')} links={privateProducts.map(toLink)} />
          <FooterColumn title={t('ui.footer.business')} links={businessProducts.map(toLink)} />

          <div>
            <FooterColumn
              title={t('ui.footer.blog')}
              links={blogCategories.slice(0, 5).map((category) => ({ to: category.path, label: category.name }))}
            />
            <div className="mt-6">
              <FooterColumn title={t('ui.footer.legal')} links={legalLinks} />
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-700 py-6 text-sm text-gray-300 sm:flex-row sm:items-center sm:justify-between">
          <p>
            {t('ui.footer.copyright', { year: new Date().getFullYear(), name: brand.legalName || brand.name })}
          </p>
          <p>
            {t('ui.footer.disclaimer')}
          </p>
        </div>
      </Container>
    </footer>
  );
}

function toLink(product: { path: string; name: string }) {
  return { to: product.path, label: product.name };
}

function FooterColumn({
  title,
  links,
}: {
  title: string;
  links: { to: string; label: string; external?: string }[];
}) {
  return (
    <div>
      <h2 className="text-sm font-semibold uppercase tracking-wide text-white">{title}</h2>
      <ul className="mt-4 space-y-2.5 text-sm text-gray-300">
        {links.map((link) => (
          <li key={link.to} className="flex items-center gap-2">
            <a href={link.to} className="hover:text-warning-300" target="_blank" rel="noopener noreferrer">
              {link.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}
